import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { dueAtInDays } from "../../lib/lane-due";
import { queryKeys } from "../../lib/queryKeys";
import type { QuickTask, TaskSuggestion } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * The top of the Tasks section: what to raise in one click, and what probably
 * should be raised.
 *
 * Two things a rep would otherwise have to think of themselves. The buttons are
 * the jobs this desk does over and over, put there by a manager so the title is
 * identical every time -- which is what makes them countable later. The
 * suggestions are the opposite case: work nobody remembers because the account
 * has gone quiet, which is exactly the account nobody is looking at.
 *
 * Both raise an ordinary Lane task through the same endpoint the composer uses,
 * so a task raised in one click is indistinguishable afterwards from one typed
 * out. That is deliberate: a shortcut that produced a second-class task would
 * mean the reporting had to know about shortcuts.
 */
export function TaskShortcuts({
  companyId,
  taskBlockedReason,
  onRaised,
}: {
  companyId: number;
  /** Why tasks cannot be raised at all, or null when they can. */
  taskBlockedReason: string | null;
  onRaised: () => void;
}) {
  const queryClient = useQueryClient();
  /** Which button is mid-flight, so only that one shows it. */
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const quickTasks = useQuery({
    queryKey: queryKeys.crmQuickTasks,
    queryFn: crmApi.quickTasks,
    staleTime: 5 * 60_000,
  });

  const suggestions = useQuery({
    queryKey: queryKeys.crmTaskSuggestions(companyId),
    queryFn: () => crmApi.taskSuggestions(companyId),
    /*
     * Short, because raising or dismissing anything changes the answer and the
     * timeline beside it refreshes on the same events. The server recomputes
     * from indexed columns, so this is cheap to ask again.
     */
    staleTime: 30_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crmTaskSuggestions(companyId),
    });
    onRaised();
  };

  const raise = useMutation({
    mutationFn: (input: { title: string; description?: string; dueInDays?: number | null }) =>
      crmApi.createTask(companyId, {
        title: input.title,
        description: input.description,
        ...(typeof input.dueInDays === "number"
          ? { dueAt: dueAtInDays(input.dueInDays) }
          : {}),
      }),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (cause) =>
      setError(
        cause instanceof ApiError ? cause.message : "Could not raise that task.",
      ),
    onSettled: () => setPending(null),
  });

  const dismiss = useMutation({
    mutationFn: (ruleId: number) =>
      crmApi.dismissTaskSuggestion(companyId, ruleId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.crmTaskSuggestions(companyId),
      }),
  });

  const buttons = quickTasks.data ?? [];
  const proposed = suggestions.data?.suggestions ?? [];
  const dormant = suggestions.data?.dormant ?? [];

  // Nothing configured and nothing to propose. Render nothing rather than an
  // empty frame explaining that a manager could configure something.
  if (buttons.length === 0 && proposed.length === 0 && dormant.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 border-b border-line px-4 py-2">
      {buttons.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          {buttons.map((task) => (
            <button
              key={task.id}
              type="button"
              disabled={taskBlockedReason !== null || pending !== null}
              title={taskBlockedReason ?? describe(task)}
              onClick={() => {
                setPending(`quick:${task.id}`);
                raise.mutate({
                  title: task.label,
                  description: task.description ?? undefined,
                  dueInDays: task.dueInDays,
                });
              }}
              className={cn(
                "rounded-full border border-line px-2.5 py-1 text-xs text-muted transition-colors",
                taskBlockedReason === null
                  ? "hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400"
                  : "cursor-not-allowed opacity-40",
              )}
            >
              {pending === `quick:${task.id}` ? "Raising…" : `+ ${task.label}`}
            </button>
          ))}
        </div>
      ) : null}

      {proposed.map((suggestion) => (
        <SuggestionCard
          key={suggestion.ruleId}
          suggestion={suggestion}
          blocked={taskBlockedReason}
          raising={pending === `rule:${suggestion.ruleId}`}
          busy={pending !== null || dismiss.isPending}
          onRaise={() => {
            setPending(`rule:${suggestion.ruleId}`);
            raise.mutate({
              title: suggestion.title,
              description: suggestion.description ?? undefined,
            });
          }}
          onDismiss={() => dismiss.mutate(suggestion.ruleId)}
        />
      ))}

      {/*
        Rules that could not run, named rather than silently skipped. A manager
        who wrote "no order in a month" and sees nothing needs to know whether
        the accounts are fine or the rule has never once been evaluated.
      */}
      {dormant.map((rule) => (
        <p key={rule.ruleId} className="text-[11px] text-faint">
          <span className="text-muted">{rule.name}</span> — {rule.reason}
        </p>
      ))}

      {error ? <p className="text-xs text-sla-breached">{error}</p> : null}
    </div>
  );
}

/** One proposal: why, and the two things that can be done about it. */
function SuggestionCard({
  suggestion,
  blocked,
  raising,
  busy,
  onRaise,
  onDismiss,
}: {
  suggestion: TaskSuggestion;
  blocked: string | null;
  raising: boolean;
  busy: boolean;
  onRaise: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed border-line px-2.5 py-2">
      <span aria-hidden className="mt-0.5 text-xs text-faint">
        ○
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-xs text-content">{suggestion.title}</p>
        {/* The reason, always. A suggestion somebody cannot argue with is one
            they can only obey or ignore, and they will ignore it. */}
        <p className="text-[11px] text-faint">{suggestion.reason}</p>
      </div>

      <button
        type="button"
        disabled={blocked !== null || busy}
        title={blocked ?? "Raise this in Lane"}
        onClick={onRaise}
        className={cn(
          "shrink-0 text-xs",
          blocked === null
            ? "text-brand-600 hover:text-brand-700"
            : "cursor-not-allowed text-faint",
        )}
      >
        {raising ? "Raising…" : "Raise"}
      </button>

      <button
        type="button"
        disabled={busy}
        title="Not for this account. It comes back if things go quiet again after some activity."
        onClick={onDismiss}
        className="shrink-0 text-xs text-faint hover:text-muted"
      >
        Dismiss
      </button>
    </div>
  );
}

/** The button's tooltip: what pressing it will actually produce. */
function describe(task: QuickTask): string {
  const due =
    task.dueInDays === null
      ? "no due date"
      : task.dueInDays === 0
        ? "due today"
        : `due in ${task.dueInDays} day${task.dueInDays === 1 ? "" : "s"}`;

  return `Raises "${task.label}" in Lane, ${due}.`;
}


